import { useCallback, useState, useMemo } from 'react';
import { useGetLessonAttendance, useSetAttendance } from 'core/hooks';

type LessonJournalType = {
  lessonId: string;
  lessonComment: string;
};

/**
 * Журнал занятия у преподавателя (ТЗ 0026).
 *
 * Переписан с локального состояния на «Apollo — источник истины». Раньше
 * чекбоксы жили в `useState`, а уезжали на сервер ТОЛЬКО пакетной кнопкой
 * «Завершить занятие»: не нажал — ничего не сохранилось, нажал — журнал
 * заперся и правку мог сделать лишь сотрудник в ERP. Теперь каждая галочка
 * сохраняется в момент клика, а откат при ошибке делает сам Apollo,
 * возвращая оптимистичное значение.
 *
 * Локальным остался только комментарий занятия — он по-прежнему уходит
 * пакетом вместе с «Занятие проведено».
 */
export const useLessonJournal = ({ lessonId, lessonComment: comment }: LessonJournalType) => {
  const [lessonComment, setLessonComment] = useState(comment);

  const { attendance: attendances, loading, error } = useGetLessonAttendance(lessonId);
  const { setAttendance, savingIds } = useSetAttendance();

  const isAllStudentsChecked = useMemo(
    () => attendances.length > 0 && !attendances.some(({ checked }) => !checked),
    [attendances],
  );

  const onChangeComment = useCallback((event) => setLessonComment(event.target.value), []);

  const onCheckStudent = useCallback(
    (attendanceId: string, next: boolean) => setAttendance(attendanceId, next),
    [setAttendance],
  );

  /**
   * Массовые действия шлём ПОСЛЕДОВАТЕЛЬНО и только по тем строкам, которые
   * реально меняются: параллельный шторм из двадцати мутаций по группе даёт
   * двадцать записей в журнале правок и ничем не помогает.
   */
  const setMany = useCallback(
    (ids: string[], value: boolean) =>
      ids.reduce(
        (chain, id) => chain.then(() => setAttendance(id, value).then(() => undefined)),
        Promise.resolve(),
      ),
    [setAttendance],
  );

  const onCheckAllStudents = useCallback(
    () =>
      setMany(
        attendances.filter(({ checked }) => !checked).map(({ attendanceId }) => attendanceId),
        true,
      ),
    [attendances, setMany],
  );

  /**
   * Spec 0013 + ТЗ 0026: «отметить заходивших».
   *
   * Кнопка остаётся, хотя клик теперь отмечает сам: авто-отметка работает
   * только внутри окна занятия, а этой кнопкой преподаватель закрывает
   * ретроспективные клики — например, когда занятие давно прошло.
   */
  const onCheckStudentsByIds = useCallback(
    (studentIds: string[]) =>
      setMany(
        attendances
          .filter(
            ({ studentId, checked }) => studentId && studentIds.includes(studentId) && !checked,
          )
          .map(({ attendanceId }) => attendanceId),
        true,
      ),
    [attendances, setMany],
  );

  const onCancel = useCallback(() => setLessonComment(''), []);

  return {
    comment: lessonComment,
    attendances,
    savingIds,
    loadingAttendances: loading,
    errorAttendances: error,
    isAllStudentsChecked,
    onChangeComment,
    onCheckStudent,
    onCheckStudentsByIds,
    onCheckAllStudents,
    onCancel,
  };
};
